import { createFileRoute, Link } from "@tanstack/react-router";
import { CalendarDays, ArrowUpRight } from "lucide-react";
import { useSiteSection } from "@/hooks/useSiteContent";
import { siteImageUrl } from "@/hooks/useSiteData";
import { postSlug } from "@/lib/blog";

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "Blog Contato — Tecnologia, streaming e novidades" },
      {
        name: "description",
        content:
          "Dicas de internet, lançamentos de streaming, esportes e novidades da Contato Internet no Sul de Santa Catarina.",
      },
      { property: "og:title", content: "Blog Contato" },
      {
        property: "og:description",
        content: "Dicas de tecnologia, streaming e novidades da sua região.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: BlogPage,
});

function BlogPage() {
  const blog = useSiteSection("blog");
  const posts = (blog.items ?? []).filter((p) => p.title);

  return (
    <>
      <section className="bg-[linear-gradient(110deg,var(--brand-sky-deep),var(--brand-deep))] py-14 text-brand-deep-foreground sm:py-20">
        <div className="mx-auto max-w-7xl px-5">
          <p className="text-[0.7rem] font-extrabold uppercase tracking-[0.2em] text-brand-yellow">
            Conteúdo
          </p>
          <h1 className="mt-2 text-3xl font-extrabold uppercase leading-tight sm:text-4xl">
            {blog.title}
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-brand-sky sm:text-base">{blog.subtitle}</p>
        </div>
      </section>

      <section className="bg-background py-12 sm:py-16">
        <div className="mx-auto grid max-w-7xl gap-6 px-5 md:grid-cols-2 lg:grid-cols-3">
          {posts.map((post) => {
            const image = siteImageUrl(post.image);
            return (
              <article
                key={post.title}
                className="flex flex-col overflow-hidden rounded-3xl border border-border bg-card transition hover:-translate-y-1 hover:shadow-lg"
              >
                {image ? (
                  <img
                    src={image}
                    alt={post.title}
                    loading="lazy"
                    className="h-44 w-full object-cover"
                  />
                ) : (
                  <div className="h-2 w-full bg-[linear-gradient(90deg,var(--brand-yellow),var(--brand-lime),var(--brand-sky))]" />
                )}
                <div className="flex flex-1 flex-col p-6">
                  {post.date ? (
                    <p className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-brand-sky">
                      <CalendarDays className="size-3.5" />
                      {post.date}
                    </p>
                  ) : null}
                  <h2 className="mt-2 text-base font-extrabold leading-snug text-brand-deep">
                    {post.title}
                  </h2>
                  <p className="mt-2 flex-1 text-sm text-muted-foreground">{post.excerpt}</p>
                  <Link
                    to="/blog/$slug"
                    params={{ slug: postSlug(post) }}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-extrabold text-primary hover:underline"
                  >
                    Ler post
                    <ArrowUpRight className="size-4" />
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </>
  );
}
